import React from 'react';
import Image from 'next/image';
import cardDefaultImg from '@images/card-default.webp';
import { TrackedAnchor } from '@/components/ancher';
import { TRACKED_EVENT_POSITION, getBlogPostPath } from '@/constants';
import type { PostSummary } from '@/utils/post-summary';
import { formatCardDate } from '@/utils/card-date';

/**
 * hover 시 **이미지만** 확대되고 텍스트는 그대로다. 이미지·텍스트 어디를 눌러도 글로
 * 이동해야 해서 카드 전체가 하나의 링크다.
 */

/**
 * 카드가 폭을 어떻게 잡는지 — 놓이는 컨테이너가 결정하므로 호출부가 넘긴다.
 *
 * - `grid`: 셀을 채운다. 목록 페이지 그리드(`grid-cols-1 md:2 lg:3`)용.
 * - `carousel`: `lg` 미만에서 **고정 320px + shrink-0**. 랜딩 카테고리 탭이
 *   `flex min-w-max` 로 가로 스크롤을 만들기 때문에, 폭이 유동적이면 넘치지 않아
 *   스크롤이 사라진다. `lg` 부터는 `li` 가 `flex-1` 이라 카드도 폭을 채운다.
 *
 * ⚠️ `carousel` 을 그리드에 쓰면 모바일에서 카드가 320px 로 고정돼 셀(≈374px) 왼쪽에
 * 붙고 오른쪽이 빈다.
 */
type BlogPostCardVariant = 'grid' | 'carousel';

const WIDTH_CLASS: Record<BlogPostCardVariant, string> = {
  grid: 'w-full',
  carousel: 'w-[320px] shrink-0 lg:w-full',
};

/** `fill` 이미지라 브라우저가 폭을 못 재서 직접 알려준다 — 틀리면 과·소해상도를 받는다 */
const IMAGE_SIZES: Record<BlogPostCardVariant, string> = {
  grid: '(min-width: 1024px) 300px, (min-width: 768px) 50vw, 100vw',
  carousel: '320px',
};

export default function BlogPostCard({
  post,
  label,
  variant = 'grid',
}: {
  post: PostSummary;
  label: string;
  variant?: BlogPostCardVariant;
}) {
  return (
    <TrackedAnchor
      href={getBlogPostPath(post.slug)}
      label={label}
      position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
      prefetch
      className={`group flex flex-col gap-4 rounded-[20px] ${WIDTH_CLASS[variant]} focus-ring`}
    >
      {/* overflow-hidden 이 확대된 이미지를 잘라 카드 크기를 유지한다 */}
      <div className="relative aspect-[300/180] w-full overflow-hidden rounded-[20px] bg-3b-background-light">
        <Image
          src={post.image || cardDefaultImg}
          alt=""
          fill
          sizes={IMAGE_SIZES[variant]}
          className="object-cover transition-transform duration-300 ease-out group-hover:scale-[1.2] motion-reduce:transform-none motion-reduce:transition-none"
        />
      </div>
      <div className="flex flex-col gap-[3px] px-1.5">
        <span className="line-clamp-2 text-sh2 font-semibold text-3b-text-primary lg:text-sh1">
          {post.title}
        </span>
        <span className="text-caption font-medium text-[#575c66] lg:text-body2">
          {formatCardDate(post.createdAt)}
        </span>
      </div>
    </TrackedAnchor>
  );
}
